/**
 * Sponsor vs the lender: the read model behind the Underwriting dashboard's
 * comparison.
 *
 * WHY this exists. The Underwriting tab showed counts — cells mapped, fields
 * "stated by the model", checklist steps — and almost never a figure. A deal
 * whose package plainly asked for $41M to refinance (Brixton) or whose sponsor
 * model stated a $9.85M NOI against $7.52M of debt service (Washington Square)
 * read as an empty page, because every panel that showed numbers needed BOTH
 * an internal and a sponsor model assigned. Underwriting is the act of
 * comparing what the sponsor says with what the lender concludes, so that is
 * what the dashboard now leads with: one row per underwriting metric, the
 * sponsor's figure, the client's own figure, and the gap between them.
 *
 * The client's column is named from the client profile (`clientLabel`, the
 * same short name the server's `side_label` gives the underwriter side), never
 * a literal: another client's workspace must not print this client's name on
 * its deal books. On this desk the owner calls the column "AREP"; that name
 * reaches the screen as the client profile's short name, not by being written
 * here. (Internally the side is still keyed `arep`, the word the code began with.)
 *
 * Nothing here is a new store. Every figure is read from what the dossier
 * already holds, and each one says where it came from:
 *
 *   Sponsor — what the package or the sponsor's model states:
 *     1. the deal record's SAVED value, when its lineage says the package
 *        stated it (email, OM, term sheet, a sponsor-model cell the fill
 *        wrote), or when a person confirmed it in the intake's review — that
 *        review decides what the package says (the server's own words: "the
 *        review decided the record's reading"), not what the lender concludes;
 *     2. the sponsor's registered model (`raw.sponsor_model`, file and cell);
 *     3. the Sponsor column of the client's UW grid, where one was read;
 *     4. a broker-stated figure kept beside its base field (`statedFigures`);
 *     5. the package's statement a person has since replaced on the deal,
 *        read back from the field's own history, so overriding the sponsor
 *        never erases what the sponsor said. Only a person's replacement
 *        (Confirmed): a statement the firm itself authored has no package
 *        statement behind it, and its own promotion event is not one — nor
 *        once a person has confirmed it (`packageAuthored`).
 *   The client — its own:
 *     1. a figure a person is typing on this dossier and has not saved;
 *     2. the deal record's saved value, when a person decided it on the deal
 *        (Confirmed there, or a statement the lineage attributes to the firm);
 *     3. the internal (underwriter-role) model's figure at its cell;
 *     4. a portfolio's roll-up of its properties' internal models, where every
 *        property states the figure;
 *     5. the client's column of its UW grid;
 *     otherwise "Not underwritten yet" — or "Reading…" while a model that may
 *     answer is still being read, and "Read failed" when that read failed.
 *
 * Typing never moves the sponsor's figure: the Sponsor side reads what the
 * deal SAVED, so the comparison stays a comparison while a person decides
 * against it. An unsaved entry is read through the save's own parser
 * (`normalizeValue`), so "40MM" or "70%" in the LTV box is the number Save
 * will write, and an entry equal to what the deal holds is no decision at all
 * (Save writes nothing for it).
 *
 * A ratio a side does not state is DERIVED from that side's own figures and
 * labeled so (DSCR = NOI ÷ annual debt service; Fully Funded = initial advance
 * + future funding; NOI = revenue − opex, the engine's own identities). Each
 * input is read as its own row reads it (the UW grid's NOI too), or derived in
 * turn. Inputs are never borrowed across sides — a lender DSCR computed from
 * the sponsor's NOI would be the sponsor's case wearing the lender's name — and
 * a ratio whose inputs are missing names them instead of being filled. A
 * derived figure is an output: unlocked, it opens its inputs, never a box (R1).
 *
 * Scale: every percent here is on the schema's 0-100 points scale. The native
 * `ltv` column keeps a fraction (0.75), so its deal value is lifted by the
 * field definition, never by its magnitude.
 */
import type {
  LoanFieldHistoryEvent,
  LoanFieldLineageField,
  StatedFigure,
} from '@/pages/pipeline-intelligence/types'
import { normalizeValue, type DealFieldDef, type SponsorFigure } from '@/services/dealFile'
import type { ModelDealView } from '@/services/underwritingWorkbooks'
import type { RolledFigure } from '@/services/uwPropertyModels'
import { attributionText, lineageSummary, stateOfLineage } from '@/components/fields/lineage'
import { isSystemField, type SlotKind } from './editableFields'
import type { UwGrid } from './economicsModel'
import { formatAmount, formatMultiple, formatPoints } from './format'

/**
 * What the client's column is called until the client profile answers. The
 * underwriter side is the LENDER — the workspace's own client — so a neutral
 * "Lender" says what the column is without naming any client.
 */
export const DEFAULT_CLIENT_LABEL = 'Lender'

/** The client's column name, from the client profile: short name, then name. */
export function clientSideLabel(profile: { shortName?: string | null; name?: string | null } | null | undefined): string {
  return profile?.shortName?.trim() || profile?.name?.trim() || DEFAULT_CLIENT_LABEL
}

export type ComparisonUnit = 'currency' | 'percent' | 'multiple' | 'count' | 'text'

export type ComparisonSide = 'sponsor' | 'arep'

/** One way to compute a ratio from figures the same side states. */
export type MetricDerivation = {
  formula: string
  inputs: readonly string[]
  /** On the display scale (a percent in points). Null when it cannot be computed. */
  compute: (inputs: number[]) => number | null
  /** How the inputs combine, for the hover that lists them: ÷ unless said. */
  operator?: string
}

export type ComparisonMetric = {
  id: string
  /** The catalogue fields that answer this row, in order: the first one a
   *  side holds is that side's figure (Rate, then Spread). */
  fields: readonly string[]
  label: string
  unit: ComparisonUnit
  /** The EditableSlot kind of the row's catalogue field. */
  slotKind: SlotKind
  /** Which half of the dashboard's comparison the row sits in. */
  group: 'request' | 'cashflow'
  derivations?: readonly MetricDerivation[]
  /** The key of the UW grid line that states this metric, where there is one. */
  uwGridKey?: string
}

/** Where a side's figure came from. */
export type FigureOrigin =
  | 'deal'
  | 'sponsor_model'
  | 'uw_grid'
  | 'broker_stated'
  | 'superseded'
  | 'internal_model'
  | 'rollup'
  | 'derived'

export type SideFigure = {
  value: number | string
  origin: FigureOrigin
  /** The catalogue field this figure answers (for a Rate/Spread row, which). */
  field: string
  /** A short caption for the cell — "Email L4", "Model B2", "Derived". */
  tag: string
  /** The full citation a reader sees on hover. */
  source: string
  /** The same citation, short: the file and cell, the email line, who decided
   *  it, or the formula. Printed under the figure where the screen has room. */
  cite: string
  /** The deal record's own value: rendered through the EditableSlot. */
  onDeal: boolean
  /** Other statements on the same side worth knowing, said on hover. */
  notes: string[]
  /** A derived figure's inputs: what an unlocked dossier opens instead of a box. */
  inputs?: readonly string[]
}

/** One figure a client ratio is worked from, as the client's column reads it. */
export type ClientInput = {
  field: string
  unit: ComparisonUnit
  /** The client's own figure for it — a decision on the deal, an entry being
   *  typed, its model's cell, its roll-up or grid, or derived in turn — or
   *  null when the client states none. Never the deal's value as such: a
   *  package figure on the deal is the sponsor's. */
  figure: SideFigure | null
  /** The deal record's saved value: what clearing the input's box puts back. */
  saved: unknown
}

export type Variance =
  | { kind: 'number'; absolute: number; relative: number | null; agrees: boolean }
  | { kind: 'text'; agrees: boolean }

/** Whose the deal record's value is, by its lineage. */
export type DealOwner = ComparisonSide | 'derived' | 'suggested' | null

export type ComparisonRow = {
  metric: ComparisonMetric
  /** The catalogue field an edit on this row writes. */
  field: string
  /** The deal record's saved value of `field`, as the record holds it. An
   *  entry box that starts empty puts this back when it is cleared, so
   *  clearing what was never the client's figure changes nothing on Save. */
  saved: unknown
  /** A person has typed a figure on `field` that differs from what the deal
   *  holds, and has not saved it. */
  typing: boolean
  sponsor: SideFigure | null
  arep: SideFigure | null
  /** Why the sponsor cell is empty, short enough for its caption: "Not
   *  stated", or the inputs a ratio needs ("Needs value"). */
  sponsorGap: string
  arepGap: string
  /** The same, in full, for the hover. */
  sponsorGapDetail: string
  arepGapDetail: string
  variance: Variance | null
  /** Why there is no variance though both sides state a figure: a Rate/Spread
   *  row where one side states the rate and the other the spread. */
  varianceNote: string | null
  /** Whose the deal record's SAVED value is. An unsaved entry does not change
   *  it: the entry is the client's, the saved value stays whoever's it was. */
  dealOwner: DealOwner
  /** The deal's value here is the engine's output — stored as Derived, or the
   *  live preview of unsaved edits — never an input box (R1). */
  dealDerivedFormula: string | null
  /** What the client's figure is worked from when its cell is an output — its
   *  own ratio, or the gap of one the deal derives: the formula, and each
   *  input as the client's own figure. An unlocked dossier opens these, never
   *  the deal's values: a box there held the sponsor's NOI under the client's
   *  heading, and clearing it removed the sponsor's figure on Save. */
  arepFormula: string | null
  arepInputs: ClientInput[]
}

export type ComparisonInput = {
  /** The deal's current value of a catalogue field — the dossier's
   *  `effectiveValue`, so an unsaved edit is read as it is typed. */
  dealValue: (field: string) => unknown
  /** The value the deal record holds before any unsaved edit. Read for a
   *  field in the draft, so the Sponsor side keeps the package's figure while
   *  a person types over it. Without it, the lineage's current value stands in. */
  savedValue?: (field: string) => unknown
  /** Fields a person is typing over and has not saved. */
  draft?: Record<string, unknown>
  /** The client's column name, from the client profile (`clientSideLabel`). */
  clientLabel?: string
  /** A value the dossier's live derivation computed and the record does not
   *  store (`isDerivedField`): the engine's arithmetic, nobody's statement. */
  isDerived?: (field: string) => boolean
  lineage: readonly LoanFieldLineageField[]
  defs: readonly Pick<DealFieldDef, 'name' | 'native' | 'kind'>[]
  sponsorModel?: Record<string, SponsorFigure> | null
  statedFigures?: readonly StatedFigure[] | null
  /** What the internal (underwriter-role) model states, when one is assigned. */
  internalModel?: ModelDealView | null
  /** A portfolio's roll-up of its properties' internal models (the underwriter
   *  side of the roll-up). A portfolio parent holds no internal model of its
   *  own: each property's is filed on the property's own record. */
  internalRollup?: { figures: readonly RolledFigure[] } | null
  /** Set while a model that may answer the client's column is still being
   *  read ("Reading the internal model"): a gap is then not yet an answer. */
  arepPending?: string | null
  /** Set when a read that may answer the client's column failed ("The
   *  property models could not be read"): a gap is then a failed read, not an
   *  absence, and says so. */
  arepUnread?: string | null
  /** What a field the deal derives is computed from (the dossier's
   *  `inputsOf`): what the client's cell opens on a row with no formula of its own. */
  inputsOf?: (field: string) => string[]
  uwGrid?: UwGrid | null
  metrics?: readonly ComparisonMetric[]
}

/** The inputs a derivation can be waiting on, short enough for a cell caption. */
const INPUT_SHORT: Record<string, string> = {
  noi: 'NOI',
  property_value: 'value',
  purchase_price: 'price',
  loan_amount: 'loan',
  annual_debt_service: 'debt service',
  total_cost: 'total cost',
  initial_funding: 'initial advance',
  future_funding: 'future funding',
  gross_revenue: 'revenue',
  operating_expenses: 'opex',
}

/** Plain-English names of the inputs a derivation can be waiting on. */
const INPUT_LABEL: Record<string, string> = {
  noi: 'in-place NOI',
  property_value: 'value (as-is)',
  purchase_price: 'purchase price',
  loan_amount: 'loan amount',
  annual_debt_service: 'annual debt service',
  total_cost: 'total project cost',
  initial_funding: 'initial advance',
  future_funding: 'future funding',
  gross_revenue: 'gross revenue',
  operating_expenses: 'operating expenses',
}

const ratio = (scale: number) => ([numerator, denominator]: number[]) =>
  denominator > 0 && Number.isFinite(numerator) ? (numerator / denominator) * scale : null

/**
 * The rows, in two halves of eight so the dashboard can set them side by side
 * at equal height. The list is the owner's (loan, fully funded, value / price,
 * NOI in-place and stabilized, cap rate, debt service, DSCR, LTV/LTC, debt
 * yield, occupancy, rate/spread, term) plus the loan purpose the Brixton
 * request states.
 */
export const COMPARISON_METRICS: readonly ComparisonMetric[] = [
  { id: 'purpose', fields: ['purpose'], label: 'Loan Purpose', unit: 'text', slotKind: 'select', group: 'request' },
  { id: 'loan_amount', fields: ['loan_amount'], label: 'Loan Amount', unit: 'currency', slotKind: 'currency', group: 'request' },
  {
    id: 'fully_funded_amount', fields: ['fully_funded_amount'], label: 'Fully Funded', unit: 'currency', slotKind: 'currency', group: 'request',
    // The engine's commitment identity (dealfields.py): never loan amount + future
    // funding, since a "Loan Amount" is sometimes already the whole commitment.
    derivations: [{
      formula: 'initial advance + future funding', inputs: ['initial_funding', 'future_funding'], operator: '+',
      compute: ([initial, future]) => initial + future,
    }],
  },
  { id: 'purchase_price', fields: ['purchase_price'], label: 'Purchase Price', unit: 'currency', slotKind: 'currency', group: 'request' },
  { id: 'property_value', fields: ['property_value'], label: 'Value (As-Is)', unit: 'currency', slotKind: 'currency', group: 'request' },
  {
    id: 'ltv', fields: ['ltv'], label: 'LTV', unit: 'percent', slotKind: 'percent', group: 'request',
    derivations: [{ formula: 'loan amount ÷ value (as-is)', inputs: ['loan_amount', 'property_value'], compute: ratio(100) }],
  },
  {
    id: 'ltc', fields: ['ltc'], label: 'LTC', unit: 'percent', slotKind: 'percent', group: 'request',
    derivations: [{ formula: 'loan amount ÷ total project cost', inputs: ['loan_amount', 'total_cost'], compute: ratio(100) }],
  },
  {
    id: 'cap_rate', fields: ['cap_rate'], label: 'Cap Rate', unit: 'percent', slotKind: 'percent', group: 'request',
    derivations: [
      { formula: 'in-place NOI ÷ value (as-is)', inputs: ['noi', 'property_value'], compute: ratio(100) },
      // An acquisition's going-in cap rate is read on the price paid when no
      // as-is value is stated; the formula says which basis it is.
      { formula: 'in-place NOI ÷ purchase price', inputs: ['noi', 'purchase_price'], compute: ratio(100) },
    ],
  },
  {
    id: 'noi', fields: ['noi'], label: 'NOI (In-Place)', unit: 'currency', slotKind: 'currency', group: 'cashflow', uwGridKey: 'noi',
    derivations: [{
      formula: 'gross revenue − operating expenses', inputs: ['gross_revenue', 'operating_expenses'], operator: '−',
      compute: ([revenue, opex]) => revenue - opex,
    }],
  },
  { id: 'noi_stabilized', fields: ['noi_stabilized'], label: 'NOI (Stabilized)', unit: 'currency', slotKind: 'currency', group: 'cashflow' },
  { id: 'occupancy', fields: ['occupancy'], label: 'Occupancy', unit: 'percent', slotKind: 'percent', group: 'cashflow' },
  { id: 'annual_debt_service', fields: ['annual_debt_service'], label: 'Debt Service', unit: 'currency', slotKind: 'currency', group: 'cashflow' },
  {
    id: 'dscr', fields: ['dscr'], label: 'DSCR', unit: 'multiple', slotKind: 'number', group: 'cashflow',
    derivations: [{ formula: 'in-place NOI ÷ annual debt service', inputs: ['noi', 'annual_debt_service'], compute: ratio(1) }],
  },
  {
    id: 'debt_yield', fields: ['debt_yield'], label: 'Debt Yield', unit: 'percent', slotKind: 'percent', group: 'cashflow',
    derivations: [{ formula: 'in-place NOI ÷ loan amount', inputs: ['noi', 'loan_amount'], compute: ratio(100) }],
  },
  { id: 'rate', fields: ['rate', 'spread'], label: 'Rate / Spread', unit: 'text', slotKind: 'text', group: 'cashflow' },
  { id: 'term_months', fields: ['term_months'], label: 'Term (Months)', unit: 'count', slotKind: 'count', group: 'cashflow' },
]

/** History states that are a document's statement rather than a person's decision. */
const PACKAGE_STATES = new Set(['extracted', 'imported'])

function present(value: unknown): boolean {
  return value !== null && value !== undefined && !(typeof value === 'string' && value.trim() === '')
}

function asNumber(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value !== 'string' || value.trim() === '') return null
  const parsed = Number(value.replace(/[$,%\s]/g, '').replace(/x$/i, ''))
  return Number.isFinite(parsed) ? parsed : null
}

/**
 * A deal value on the display scale, or null when the deal states none.
 *
 * `typed`: the value is an unsaved entry, read through the save's own parser —
 * "40MM" is $40,000,000 and "70%" in the native LTV box is the fraction 0.70,
 * exactly what Save stores. A second parse here once read "70%" as 7,000 points
 * and "40MM" as no figure at all. A STORED string is never sent through it: the
 * record's "0.75" is already the column's fraction.
 */
function displayScale(
  field: string,
  value: unknown,
  unit: ComparisonUnit,
  def: Pick<DealFieldDef, 'native' | 'kind'> | undefined,
  typed = false,
): number | string | null {
  if (!present(value)) return null
  if (unit === 'text') return String(value).trim()
  const n = asNumber(typed && def && typeof value === 'string' ? normalizeValue(def, value) : value)
  if (n === null) return null
  // `loans.loan_amount` is NOT NULL: 0 is how a deal records "no amount stated".
  if (field === 'loan_amount' && n === 0) return null
  return def?.native && def.kind === 'percent' ? n * 100 : n
}

/** The figure a cell prints, on the metric's unit. */
export function formatFigure(value: number | string | null | undefined, unit: ComparisonUnit): string {
  if (value === null || value === undefined || value === '') return '—'
  if (typeof value === 'string') return value
  switch (unit) {
    case 'currency':
      return formatAmount(value)
    case 'percent':
      return formatPoints(value)
    case 'multiple':
      return formatMultiple(value)
    case 'count':
      return value.toLocaleString('en-US')
    default:
      return String(value)
  }
}

function signed(value: number, text: string): string {
  if (value > 0) return `+${text}`
  if (value < 0) return `−${text}`
  return text
}

/** Client − Sponsor in the metric's own unit: dollars, points, turns, months. */
export function formatVarianceAbsolute(variance: Variance, unit: ComparisonUnit): string {
  if (variance.kind === 'text') return variance.agrees ? 'Agrees' : 'Differs'
  if (variance.agrees) return '—'
  const size = Math.abs(variance.absolute)
  switch (unit) {
    case 'currency':
      return signed(variance.absolute, formatAmount(size))
    case 'percent':
      return signed(variance.absolute, `${size.toFixed(2)} pts`)
    case 'multiple':
      return signed(variance.absolute, `${size.toFixed(2)}x`)
    default:
      return signed(variance.absolute, size.toLocaleString('en-US'))
  }
}

/** The gap as a share of the sponsor's figure, or "Agrees". */
export function formatVarianceRelative(variance: Variance): string {
  if (variance.agrees) return 'Agrees'
  if (variance.kind === 'text') return 'Differs'
  if (variance.relative === null) return 'Differs'
  return signed(variance.relative, `${Math.abs(variance.relative * 100).toFixed(1)}%`)
}

/** How close two figures must be to read as the same number on screen. */
const AGREEMENT: Record<Exclude<ComparisonUnit, 'text'>, number> = {
  currency: 0.5,
  percent: 0.005,
  multiple: 0.005,
  count: 0.5,
}

function within(a: number, b: number, unit: ComparisonUnit): boolean {
  return unit !== 'text' && Math.abs(a - b) < AGREEMENT[unit]
}

function normalizedText(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9.%+]+/g, ' ').trim()
}

export function varianceOf(
  sponsor: SideFigure | null,
  arep: SideFigure | null,
  unit: ComparisonUnit,
): Variance | null {
  if (!sponsor || !arep) return null
  if (unit === 'text' || typeof sponsor.value === 'string' || typeof arep.value === 'string') {
    return { kind: 'text', agrees: normalizedText(String(sponsor.value)) === normalizedText(String(arep.value)) }
  }
  const absolute = arep.value - sponsor.value
  return {
    kind: 'number',
    absolute,
    relative: sponsor.value !== 0 ? absolute / Math.abs(sponsor.value) : null,
    agrees: within(arep.value, sponsor.value, unit),
  }
}

function dateText(iso: string | null | undefined): string {
  if (!iso) return ''
  const date = new Date(iso)
  return Number.isNaN(date.getTime())
    ? String(iso)
    : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

/** A person's decision on the intake's review of the package, as the lineage
 *  records it on the deal: the mirror writes its source as "intake record
 *  INQ-… · Data Intelligence Review decision by …" (`_mirror_review_decision`),
 *  and a reading decided on the record itself names the screen ("Smart Review"). */
const PACKAGE_REVIEW = /^intake record\b|^(?:smart|data intelligence|data) review\b/i

type PackageReview = { screen: string; by: string | null; at: string | null; record: string | null }

/**
 * The review that confirmed this value, when it was the intake's review of the
 * package rather than a decision on the deal.
 *
 * That review answers "what does the package say?" — confirm a reading, pick
 * between two, correct a misread figure — and the server keeps it apart from
 * the deal's own confirmed values (rule R5). Calling it the lender's
 * underwriting put Brixton's emailed $41M under the client's name and marked
 * the comparison "Agrees" before anyone had underwritten anything.
 */
function packageReview(row: LoanFieldLineageField | undefined): PackageReview | null {
  if (!row || stateOfLineage(row, 'suggested') !== 'confirmed') return null
  const via = (row.confirmation?.via ?? row.source ?? '').trim()
  if (!PACKAGE_REVIEW.test(via)) return null
  return {
    screen: /data (?:intelligence )?review/i.test(via) ? 'Data Intelligence Review' : 'Smart Review',
    by: row.confirmation?.by ?? row.editorEmail ?? null,
    at: row.confirmation?.at ?? row.updatedAt ?? null,
    record: /intake record\s+(\S+)/i.exec(via)?.[1] ?? row.provenance?.ingestionId ?? null,
  }
}

/**
 * Whose the deal's SAVED value is. A value the lineage cannot account for is
 * put on the sponsor's side, never the client's — calling an unsourced number
 * the lender's conclusion is the one error this table exists to prevent. A
 * value a person is typing is read separately (`typedValue`) and never changes
 * this: the saved figure stays whoever's it was until the entry is saved.
 */
function ownerOf(
  held: boolean,
  row: LoanFieldLineageField | undefined,
  previewDerived: boolean,
): DealOwner {
  if (!held) return null
  if (previewDerived) return 'derived'
  if (!row) return 'sponsor'
  const state = stateOfLineage(row, 'suggested')
  if (state === 'confirmed') return packageReview(row) ? 'sponsor' : 'arep'
  if (state === 'stated') return row.provenance?.attribution === 'client' ? 'arep' : 'sponsor'
  if (state === 'derived') return 'derived'
  if (state === 'suggested') return 'suggested'
  // Conflicted, or a state the agreed vocabulary does not name: a package
  // value nobody has decided, so it is not the client's.
  return 'sponsor'
}

/** "Summary!B2" → "B2": the sheet is on hover, and a caption must stay one line. */
function cellOnly(ref: string): string {
  const bang = ref.lastIndexOf('!')
  return bang >= 0 ? ref.slice(bang + 1) : ref
}

/** The short caption of a package-stated deal value: the document, and where. */
function packageTag(row: LoanFieldLineageField | undefined): string {
  const prov = row?.provenance
  if (!prov) return 'On the deal'
  if (prov.attribution === 'sponsor') {
    // A sponsor-model fill names its cell in the document line
    // ("Washington Square - Sponsor Model.xlsx Annual_Summary!C16").
    const cell = prov.cellRef ?? /!([A-Z]{1,3}\d+)\s*$/.exec(prov.document ?? '')?.[1]
    return cell ? `Model ${cellOnly(cell)}` : 'Sponsor model'
  }
  const document = (prov.document ?? '').toLowerCase()
  if (document.startsWith('email subject')) return 'Email subject'
  if (document.startsWith('email') || prov.method === 'message_received') {
    return prov.lineNo != null && prov.lineNo !== '' ? `Email L${prov.lineNo}` : 'Email'
  }
  if (prov.cellRef) return `Cell ${cellOnly(prov.cellRef)}`
  if (prov.page) return `Doc p.${prov.page}`
  return prov.document ? 'Document' : 'Package'
}

function modelCitation(figure: Pick<SponsorFigure, 'filename' | 'sheet' | 'cell'>): string {
  // Cell first: in a narrow cell the filename is what truncates, never the cell.
  return `${figure.sheet}!${figure.cell} · ${figure.filename}`
}

/**
 * The short citation of a package-stated deal value: the place first, then
 * the document, so a truncated line still names the email line or the cell.
 */
function packageCite(row: LoanFieldLineageField | undefined): string {
  const prov = row?.provenance
  const summary = row ? lineageSummary(row) : ''
  if (!prov) return summary || 'No source recorded'
  const when = dateText(prov.statedAt)
  const document = prov.document ?? ''
  // "<file> <Sheet>!<Cell>", a quoted sheet name allowed: "<file> 'T-12 Summary'!B4".
  const workbookCell = /^(.*\S)\s+('[^']+'|[^\s!]+)!([A-Z]{1,3}\d+)\s*$/.exec(document)
  if (prov.attribution === 'sponsor' && workbookCell) {
    return [`${workbookCell[2]}!${workbookCell[3]}`, workbookCell[1], when].filter(Boolean).join(' · ')
  }
  if (document.toLowerCase().startsWith('email')) {
    const part = document.toLowerCase().startsWith('email subject') ? 'Email subject' : 'Email body'
    return [part, prov.lineNo != null && prov.lineNo !== '' ? `line ${prov.lineNo}` : '', when, prov.sender ?? '']
      .filter(Boolean)
      .join(' · ')
  }
  return summary || document || 'No source recorded'
}

/**
 * The newest statement the package made, read back from the field's history.
 *
 * The newest document event decides, and an empty one is a retraction: the
 * fill that wrote NOI from one month of a T-12, or from a model since
 * withdrawn, was taken back with a null value under the same document state,
 * and skipping past that null brought the withdrawn figure back as the
 * package's. The state read is `valueState`, the server's normalised word: a
 * machine backfill stores `approved` (`storedState`) but is a document's
 * reading, not a person's decision, and testing the stored word dropped it.
 */
function packageStatement(row: LoanFieldLineageField | undefined): LoanFieldHistoryEvent | null {
  const events = [...(row?.history ?? [])].sort(
    (a, b) => new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime(),
  )
  const newest = events.find((event) => PACKAGE_STATES.has(String(event.valueState ?? event.storedState)))
  return newest && present(newest.newValue) ? newest : null
}

/** A history event as the server will send it once each event says whose
 *  figure it is and what kind (not yet sent by `_serialize_field_event`). */
type AttributedEvent = LoanFieldHistoryEvent & { attribution?: string | null; state?: string | null }

/**
 * Is a document event the package's own statement? Promotion writes every
 * value it carries as `extracted` — the firm's own term sheet, a figure the
 * engine derived or suggested, as much as the sponsor's email — so the stored
 * word alone never says the package stated it.
 *
 * Where the server says whose the event's figure is (`attribution`) and what
 * kind (`state`, the agreed vocabulary), that decides: a Stated figure from
 * outside the firm, 'unattributed' included — a line in the package nobody has
 * placed is still the package's, as it is for the current value (`ownerOf`).
 * Until it does, the one thing known is the document the person agreed with
 * when the confirmed value matches one: a firm-authored figure confirmed as it
 * stood is the firm's own, and its promotion event carries that same figure.
 * Read back, it put the firm's $39M under Sponsor as "Package" beside a false
 * "Agrees". (A promoted derived or suggested figure a person later replaced
 * cannot be told apart without the per-event words.)
 */
function packageAuthored(row: LoanFieldLineageField | undefined, event: LoanFieldHistoryEvent, sameAsDeal: boolean): boolean {
  const said = event as AttributedEvent
  if (said.attribution !== undefined || said.state !== undefined) {
    return stateOfLineage(said) === 'stated' && said.attribution !== 'client'
  }
  return !(sameAsDeal && row?.provenance?.attribution === 'client')
}

type Context = {
  input: ComparisonInput
  metrics: readonly ComparisonMetric[]
  lineage: Map<string, LoanFieldLineageField>
  defs: Map<string, Pick<DealFieldDef, 'name' | 'native' | 'kind'>>
  draft: Record<string, unknown>
  /** The client's column name. */
  client: string
}

/** A field's unit: its row's, or money for an input no row shows (total project cost). */
function unitOf(ctx: Context, field: string): ComparisonUnit {
  return ctx.metrics.find((item) => item.fields.includes(field))?.unit ?? 'currency'
}

/** The deal record's stored value of a field, before any unsaved edit. */
function savedRaw(ctx: Context, field: string): unknown {
  if (!(field in ctx.draft)) return ctx.input.dealValue(field)
  return ctx.input.savedValue ? ctx.input.savedValue(field) : ctx.lineage.get(field)?.currentValue
}

/** The deal's SAVED figure and whose it is. Typing over it changes neither. */
function savedFigure(ctx: Context, field: string, unit: ComparisonUnit) {
  const drafted = field in ctx.draft
  const value = displayScale(field, savedRaw(ctx, field), unit, ctx.defs.get(field))
  const row = ctx.lineage.get(field)
  // A live preview is the engine's arithmetic over the inputs as they stand;
  // a field a person is typing has none.
  const previewDerived = !drafted && (ctx.input.isDerived?.(field) ?? false)
  return { value, row, owner: ownerOf(value !== null, row, previewDerived), previewDerived }
}

function sameFigure(a: number | string | null, b: number | string | null): boolean {
  if (a === null || b === null) return a === b
  if (typeof a === 'number' && typeof b === 'number') return Math.abs(a - b) < 1e-9
  return String(a) === String(b)
}

/**
 * The figure a person has typed on a field and not saved, or null when there
 * is no such entry. An entry equal to what the deal holds is no entry: Save
 * writes nothing for it (`buildPatch`), so it is no decision either. `value`
 * is null for an entry that is no figure — blank (Save clears the field) or
 * text that is not a number.
 */
function typedValue(ctx: Context, field: string, unit: ComparisonUnit): { value: number | string | null } | null {
  if (!(field in ctx.draft)) return null
  const def = ctx.defs.get(field)
  const value = displayScale(field, ctx.draft[field], unit, def, true)
  const saved = displayScale(field, savedRaw(ctx, field), unit, def)
  return sameFigure(value, saved) ? null : { value }
}

/** The client's unsaved entry, as a figure. */
function unsavedFigure(field: string, value: number | string): SideFigure {
  return {
    value,
    origin: 'deal',
    field,
    tag: 'Unsaved',
    source: 'Typed on this dossier and not saved yet',
    cite: 'Not saved yet',
    onDeal: true,
    notes: [],
  }
}

/** The sponsor's figure for one catalogue field, without deriving it. */
function sponsorDirect(ctx: Context, field: string, unit: ComparisonUnit, metric?: ComparisonMetric): SideFigure | null {
  const { value, row, owner } = savedFigure(ctx, field, unit)
  const model = ctx.input.sponsorModel?.[field]
  const modelValue = model && typeof model.value === 'number' ? model.value : null

  if (value !== null && owner === 'sponsor') {
    const notes: string[] = []
    if (model && modelValue !== null) {
      notes.push(
        typeof value === 'number' && within(value, modelValue, unit)
          ? `Also stated in the sponsor's model at ${modelCitation(model)}.`
          : `The sponsor's model states ${formatFigure(modelValue, unit)} at ${modelCitation(model)}.`,
      )
    }
    const review = packageReview(row)
    if (review) {
      // Confirmed as the package's reading: still the sponsor's figure, and
      // the caption says a person checked it rather than naming a line.
      return {
        value,
        origin: 'deal',
        field,
        tag: 'Reviewed',
        source: `The package's reading, confirmed by ${review.by ?? 'a person'} in the ${review.screen}${
          review.record ? ` (intake record ${review.record})` : ''
        } — a decision on what the package states, not ${ctx.client}'s underwriting`,
        cite: [review.screen, review.by, dateText(review.at)].filter(Boolean).join(' · '),
        onDeal: true,
        notes,
      }
    }
    const who = attributionText(row?.provenance?.attribution) ?? 'The package'
    return {
      value,
      origin: 'deal',
      field,
      tag: packageTag(row),
      source: row
        ? `${who} — ${lineageSummary(row) || 'on the deal record'}`
        : 'On the deal record — no source is recorded for this value',
      cite: packageCite(row),
      onDeal: true,
      notes,
    }
  }

  if (model && modelValue !== null) {
    return {
      value: modelValue,
      origin: 'sponsor_model',
      field,
      tag: `Model ${model.cell}`,
      source: `The sponsor's model — ${modelCitation(model)}${model.computed ? ' (a formula cell)' : ''}`,
      cite: modelCitation(model),
      onDeal: false,
      notes: (model.alternatives ?? []).map(
        (alt) => `Also stated as ${formatFigure(alt.value, unit)} at ${alt.sheet}!${alt.cell}.`,
      ),
    }
  }

  const grid = ctx.input.uwGrid
  const gridLine = metric?.uwGridKey ? grid?.lines.find((line) => line.key === metric.uwGridKey) : undefined
  const gridValue = gridLine?.values.sponsor
  if (grid && gridLine && typeof gridValue === 'number') {
    const cell = gridLine.cells.sponsor
    return {
      value: gridValue,
      origin: 'uw_grid',
      field,
      tag: cell ? `UW grid ${cellOnly(cell)}` : 'UW grid',
      source: `The Sponsor column of ${grid.workbook} · ${grid.sheet}${cell ? `!${cellOnly(cell)}` : ''}`,
      cite: `${grid.workbook} · ${grid.sheet}${cell ? `!${cellOnly(cell)}` : ''}`,
      onDeal: false,
      notes: [],
    }
  }

  const broker = ctx.input.statedFigures?.find((item) => item.base === field && typeof item.stated === 'number')
  if (broker && typeof broker.stated === 'number') {
    return {
      value: broker.stated,
      origin: 'broker_stated',
      field,
      tag: 'Broker',
      source: `${broker.label} — kept as the broker stated it${broker.note ? `. ${broker.note}` : ''}`,
      cite: broker.label,
      onDeal: false,
      notes: [],
    }
  }

  // The package's own statement, after a person replaced it on the deal: what
  // the sponsor said is not erased by the client disagreeing with it. Read
  // whether or not a further edit is being typed — `owner` is the SAVED
  // value's, so a draft no longer hides the sponsor's figure mid-edit.
  // Only a person's replacement (Confirmed). A value the firm itself stated
  // (its own term sheet, an email from inside the firm) is the client's too,
  // but its history's document event is that same statement's promotion: read
  // back here, it printed the firm's figure under Sponsor and marked it Agrees.
  // The same holds for a firm statement a person then confirmed, and for
  // whatever else promotion wrote as a document's reading (`packageAuthored`).
  if (owner === 'arep' && stateOfLineage(row, 'suggested') === 'confirmed') {
    const event = packageStatement(row)
    const stated = event ? displayScale(field, event.newValue, unit, ctx.defs.get(field)) : null
    if (event && stated !== null && packageAuthored(row, event, sameFigure(stated, value))) {
      return {
        value: stated,
        origin: 'superseded',
        field,
        tag: 'Package',
        source: `The package's statement${event.source ? ` (${event.source})` : ''}${
          event.createdAt ? ` · ${dateText(event.createdAt)}` : ''
        } — the deal record now holds a person's figure`,
        cite: [event.source, dateText(event.createdAt)].filter(Boolean).join(' · ') || 'The package',
        onDeal: false,
        notes: [],
      }
    }
  }
  return null
}

/** The client's roll-up of one field across a portfolio's properties, when one was read. */
function rolledFigure(ctx: Context, field: string): RolledFigure | undefined {
  return ctx.input.internalRollup?.figures.find((item) => item.field === field)
}

/** The client's figure for one catalogue field, without deriving it. */
function arepDirect(ctx: Context, field: string, unit: ComparisonUnit, metric?: ComparisonMetric): SideFigure | null {
  const internal = ctx.input.internalModel?.sections
    .flatMap((section) => section.rows)
    .find((item) => item.field === field)
  const internalNote = (value: number | string) =>
    internal && typeof value === 'number' && !within(internal.modelValue, value, unit)
      ? [`The internal model states ${formatFigure(internal.modelValue, unit)} at ${internal.provenance.filename} · ${internal.provenance.sheet}!${internal.provenance.cell}.`]
      : []

  // A figure being typed is the person's decision in the making. An entry that
  // is no figure (blank, or text) stands for nothing — it never falls back to
  // the internal model, which would put a number in the box's column that the
  // person just removed.
  const typed = typedValue(ctx, field, unit)
  if (typed) return typed.value === null ? null : { ...unsavedFigure(field, typed.value), notes: internalNote(typed.value) }

  const { value, row, owner } = savedFigure(ctx, field, unit)
  if (value !== null && owner === 'arep') {
    return {
      value,
      origin: 'deal',
      field,
      tag: row?.confirmation?.by ?? (stateOfLineage(row, 'suggested') === 'stated' ? 'Firm document' : 'Decided'),
      source: (row ? lineageSummary(row) : '') || 'Decided on the deal record',
      cite: (row ? lineageSummary(row) : '') || 'Decided on the deal record',
      onDeal: true,
      notes: internalNote(value),
    }
  }

  if (internal) {
    const sheet = internal.provenance.sheet ?? ''
    const cell = internal.provenance.cell ?? ''
    return {
      value: internal.modelValue,
      origin: 'internal_model',
      field,
      tag: cell ? `Internal ${cell}` : 'Internal model',
      source: `The internal model — ${internal.provenance.filename}${sheet || cell ? ` · ${sheet}!${cell}` : ''}${
        internal.provenance.computed ? ' (a formula cell)' : ''
      }`,
      cite: `${internal.provenance.filename}${sheet || cell ? ` · ${sheet}!${cell}` : ''}`,
      onDeal: false,
      notes: [],
    }
  }

  // A portfolio's own figure, rolled up from each property's internal model —
  // only where every property states it (`value`; a partial sum is no deal
  // figure, and its gap says so in `sideFigure`).
  const rolled = rolledFigure(ctx, field)
  if (rolled && typeof rolled.value === 'number') {
    const properties = rolled.contributors.filter((item) => item.rolled).map((item) => item.name)
    const coverage = `${rolled.coverage.have} of ${rolled.coverage.of} properties`
    return {
      value: rolled.value,
      origin: 'rollup',
      field,
      tag: 'Roll-up',
      source: `${ctx.client}'s roll-up of the property models — ${rolled.method}, ${coverage}${
        properties.length ? `: ${properties.join(', ')}` : ''
      }`,
      cite: `${rolled.method} · ${coverage}`,
      onDeal: false,
      notes: [],
    }
  }

  const grid = ctx.input.uwGrid
  const gridLine = metric?.uwGridKey ? grid?.lines.find((line) => line.key === metric.uwGridKey) : undefined
  const gridValue = gridLine?.values.arep
  if (grid && gridLine && typeof gridValue === 'number') {
    const cell = gridLine.cells.arep
    return {
      value: gridValue,
      origin: 'uw_grid',
      field,
      tag: cell ? `UW grid ${cellOnly(cell)}` : 'UW grid',
      source: `The ${ctx.client} column of ${grid.workbook} · ${grid.sheet}${cell ? `!${cellOnly(cell)}` : ''}`,
      cite: `${grid.workbook} · ${grid.sheet}${cell ? `!${cellOnly(cell)}` : ''}`,
      onDeal: false,
      notes: [],
    }
  }
  return null
}

/**
 * One input of a side's derivation: that side's own figure for it, read
 * exactly as the input's own row reads it — so a DSCR uses the UW grid's NOI
 * the NOI row shows, rather than reading "Needs NOI" beneath it — or, when
 * the input is itself a derived row (NOI from revenue and opex), worked from
 * that side's own figures one level down.
 */
function inputFigure(ctx: Context, side: ComparisonSide, field: string, depth: number): SideFigure | null {
  const metric = ctx.metrics.find((item) => item.fields.includes(field))
  const unit = unitOf(ctx, field)
  const figure = (side === 'sponsor' ? sponsorDirect : arepDirect)(ctx, field, unit, metric)
  if (figure || depth > 0 || !metric?.derivations?.length) return figure
  // An entry that is no figure stands for nothing, here as on its own row.
  if (side === 'arep' && typedValue(ctx, field, unit)) return null
  return derive(ctx, side, metric, depth + 1).figure
}

/** A ratio computed from one side's own figures, or the inputs it is waiting on. */
function derive(
  ctx: Context,
  side: ComparisonSide,
  metric: ComparisonMetric,
  depth = 0,
): { figure: SideFigure | null; waitingOn: string[] | null; formula: string | null } {
  let firstGap: string[] | null = null
  let firstFormula: string | null = null
  let anyInput = false
  for (const derivation of metric.derivations ?? []) {
    const figures = derivation.inputs.map((field) => inputFigure(ctx, side, field, depth))
    const values = figures.map((figure) => (figure && typeof figure.value === 'number' ? figure.value : null))
    if (values.some((value) => value !== null)) anyInput = true
    if (values.every((value): value is number => value !== null)) {
      const result = derivation.compute(values)
      if (result !== null && Number.isFinite(result)) {
        const parts = derivation.inputs.map(
          (field, index) => `${INPUT_LABEL[field] ?? field} ${formatFigure(values[index], unitOf(ctx, field))} (${figures[index]?.tag})`,
        )
        return {
          figure: {
            value: Math.round(result * 10_000) / 10_000,
            origin: 'derived',
            field: metric.fields[0],
            tag: 'Derived',
            source: `Derived: ${derivation.formula} — ${parts.join(` ${derivation.operator ?? '÷'} `)}, all ${
              side === 'sponsor' ? "the sponsor's" : `${ctx.client}'s`
            } own figures`,
            cite: derivation.formula,
            onDeal: false,
            notes: [],
            inputs: derivation.inputs,
          },
          waitingOn: null,
          formula: derivation.formula,
        }
      }
    }
    if (firstGap === null) {
      firstGap = derivation.inputs.filter((_, index) => values[index] === null)
      firstFormula = derivation.formula
    }
  }
  return { figure: null, waitingOn: anyInput ? firstGap : null, formula: firstFormula }
}

type SideResult = { figure: SideFigure | null; gap: string; detail: string }

function sideFigure(ctx: Context, side: ComparisonSide, metric: ComparisonMetric): SideResult {
  const direct = side === 'sponsor' ? sponsorDirect : arepDirect
  if (side === 'arep') {
    // An entry that is no figure says what it is, rather than reading as a
    // deal nobody has underwritten.
    for (const field of metric.fields) {
      const typed = typedValue(ctx, field, metric.unit)
      if (!typed || typed.value !== null) continue
      const text = String(ctx.draft[field] ?? '').trim()
      return text === ''
        ? {
            figure: null,
            gap: 'Clears on save',
            detail: `${metric.label} is cleared on this dossier and not saved yet: saving removes the figure from the deal.`,
          }
        : {
            figure: null,
            gap: 'Not a figure',
            detail: `"${text}" is not a figure Save can store for ${metric.label.toLowerCase()}.`,
          }
    }
  }
  for (const field of metric.fields) {
    const figure = direct(ctx, field, metric.unit, metric)
    if (figure) {
      // A Rate/Spread row says which of the two the side states.
      if (metric.fields.length > 1 && field !== metric.fields[0]) figure.notes.unshift(`Stated as the ${field.replace(/_/g, ' ')}.`)
      return { figure, gap: '', detail: '' }
    }
  }
  const who = side === 'sponsor' ? 'the sponsor' : ctx.client
  const dealNote = dealDerivedNote(ctx, metric, who)
  let waiting: SideResult | null = null
  if (metric.derivations?.length) {
    const derived = derive(ctx, side, metric)
    if (derived.figure) return { figure: derived.figure, gap: '', detail: '' }
    if (derived.waitingOn?.length) {
      waiting = {
        figure: null,
        gap: `Needs ${derived.waitingOn.map((field) => INPUT_SHORT[field] ?? field).join(', ')}`,
        detail: `${metric.label} is not stated by ${who}, and is derived as ${derived.formula}: ${who} states no ${derived.waitingOn
          .map((field) => INPUT_LABEL[field] ?? field)
          .join(' or ')}.${dealNote}`,
      }
    }
  }
  // A model that may answer is still being read: the gap is not an answer yet.
  // "Not underwritten" here was a definite statement that held only until the
  // internal model's read landed a moment later.
  const pending = side === 'arep' ? ctx.input.arepPending?.trim() : ''
  if (pending) {
    return {
      figure: null,
      gap: 'Reading…',
      detail: `${pending}: ${ctx.client}'s ${metric.label.toLowerCase()} is not known until it has been read.`,
    }
  }
  // A read that may answer failed: the gap is not an absence either. "No
  // internal model states it" here was said of a model nobody could read.
  const unread = side === 'arep' ? ctx.input.arepUnread?.trim() : ''
  if (unread) {
    return {
      figure: null,
      gap: 'Read failed',
      detail: `${unread}, so ${ctx.client}'s ${metric.label.toLowerCase()} is not known — a failed read, not a figure nobody states.${dealNote}`,
    }
  }
  if (waiting) return waiting
  if (side === 'sponsor') {
    return { figure: null, gap: 'Not stated', detail: `Neither the package nor the sponsor's model states ${metric.label.toLowerCase()}.${dealNote}` }
  }
  // A portfolio's roll-up that some properties do not answer yet: not the
  // deal's figure, and not "nothing" either.
  const rolled = metric.fields.map((field) => rolledFigure(ctx, field)).find(Boolean)
  if (rolled) {
    const missing = rolled.missing.map((item) => item.name)
    return {
      figure: null,
      gap: `Rolled ${rolled.coverage.have} of ${rolled.coverage.of}`,
      detail: `${ctx.client}'s roll-up of the property models states ${metric.label.toLowerCase()} for ${rolled.coverage.have} of ${
        rolled.coverage.of
      } properties${typeof rolled.partialValue === 'number' ? ` (${formatFigure(rolled.partialValue, metric.unit)} so far)` : ''}${
        missing.length ? `; not ${missing.join(', ')}` : ''
      } — not the deal's figure until every property states it.${dealNote}`,
    }
  }
  return {
    figure: null,
    gap: 'Not underwritten',
    detail: `Not underwritten yet: no person has decided ${metric.label.toLowerCase()} on the deal, and no ${
      ctx.input.internalRollup ? "property's internal model" : 'internal model'
    } states it.${dealNote}`,
  }
}

/**
 * Said in a gap's hover when the deal holds the engine's own figure for the
 * row and the side's figures do not give it: the deal's figure is real (never
 * hidden behind "Not stated"), but it is arithmetic over the deal's figures,
 * not that side's statement — so it is named, not printed under the side.
 */
function dealDerivedNote(ctx: Context, metric: ComparisonMetric, who: string): string {
  for (const field of metric.fields) {
    const { value, row, owner, previewDerived } = savedFigure(ctx, field, metric.unit)
    if (owner !== 'derived' || value === null) continue
    const how = previewDerived ? 'recalculates from the unsaved edits' : `derives (${row?.derivation?.formula || 'from other deal fields'})`
    return ` The deal record holds ${formatFigure(value, metric.unit)}, which the engine ${how} — arithmetic over the deal's figures, not ${who}'s own statement.`
  }
  return ''
}

/** Every row of the comparison, in the order the metrics are listed. */
export function buildSponsorArepComparison(input: ComparisonInput): ComparisonRow[] {
  const metrics = input.metrics ?? COMPARISON_METRICS
  const ctx: Context = {
    input,
    metrics,
    lineage: new Map(input.lineage.map((row) => [row.fieldName, row])),
    defs: new Map(input.defs.map((def) => [def.name, def])),
    draft: input.draft ?? {},
    client: input.clientLabel?.trim() || DEFAULT_CLIENT_LABEL,
  }
  return metrics.map((metric) => {
    const sponsor = sideFigure(ctx, 'sponsor', metric)
    const arep = sideFigure(ctx, 'arep', metric)
    const { sponsorFigure, arepFigure, varianceNote, field: common } = sameQuantity(ctx, metric, sponsor.figure, arep.figure)
    // The field an edit writes: the one being typed, else the one both sides
    // were read on (a box for the rate captioned with the client's spread held
    // 7.25% under "Internal C20"), else one the deal already holds, else the first.
    const field =
      metric.fields.find((name) => typedValue(ctx, name, metric.unit) !== null) ??
      common ??
      metric.fields.find((name) => name in ctx.draft) ??
      metric.fields.find((name) => present(savedRaw(ctx, name))) ??
      metric.fields[0]
    const { owner, row, previewDerived } = savedFigure(ctx, field, metric.unit)
    const dealDerivedFormula =
      owner === 'derived' && !(field in ctx.draft)
        ? previewDerived
          ? 'recalculated from your unsaved edits'
          : row?.derivation?.formula || 'computed from other deal fields'
        : null
    const ratio = clientRatio(ctx, metric, field, arepFigure, dealDerivedFormula)
    return {
      metric,
      field,
      saved: savedRaw(ctx, field),
      typing: typedValue(ctx, field, metric.unit) !== null,
      sponsor: sponsorFigure,
      arep: arepFigure,
      sponsorGap: sponsor.gap,
      arepGap: arep.gap,
      sponsorGapDetail: sponsor.detail,
      arepGapDetail: arep.detail,
      variance: varianceNote ? null : varianceOf(sponsorFigure, arepFigure, metric.unit),
      varianceNote,
      dealOwner: owner,
      // The engine's figure is an output a person may not type over (R1):
      // stored as Derived, or the live preview recalculated from unsaved edits
      // — the same output a moment before it is stored, which EditableSlot
      // draws as an output on every other tab. A box over the preview let a
      // hand-typed DSCR replace the one its inputs give.
      dealDerivedFormula,
      arepFormula: ratio.formula,
      arepInputs: ratio.inputs,
    }
  })
}

/**
 * What the client's figure on a row is worked from, as the client's column
 * reads each input (`inputFigure`, exactly as `derive` read it): the formula
 * the client's own ratio used, else the row's first formula, whose inputs its
 * gap is waiting on, else — for a field only the deal derives — the deal's
 * recorded inputs. Each input carries the client's figure, never the deal's
 * value: the deal's NOI may be the sponsor's.
 */
function clientRatio(
  ctx: Context,
  metric: ComparisonMetric,
  field: string,
  figure: SideFigure | null,
  dealDerivedFormula: string | null,
): { formula: string | null; inputs: ClientInput[] } {
  const derivations = metric.derivations ?? []
  const used = figure?.origin === 'derived' ? derivations.find((item) => item.formula === figure.cite) : undefined
  const derivation = used ?? derivations[0]
  const formula = derivation?.formula ?? dealDerivedFormula
  const fields = derivation
    ? derivation.inputs
    : dealDerivedFormula
      ? [...new Set(ctx.input.inputsOf?.(field) ?? [])].filter((name) => name && name !== field && !isSystemField(name))
      : []
  return {
    formula: fields.length ? formula : null,
    inputs: fields.map((name) => ({
      field: name,
      unit: unitOf(ctx, name),
      figure: inputFigure(ctx, 'arep', name, 0),
      saved: savedRaw(ctx, name),
    })),
  }
}

/**
 * A row answered by more than one field (Rate, then Spread) compares like
 * with like: each side's first figure is its own pick, so the sponsor's
 * "S+350" stood against the client's 7.25% all-in rate and read "Differs".
 * When the two picked different fields, both are read on the first field both
 * sides state, and that field is the row's (`field`: the one its box edits);
 * when there is none, there is no variance, and the note says why. An entry
 * being typed stays where it is typed — the box shows it — so then only the
 * sponsor's side may move to meet it.
 */
function sameQuantity(
  ctx: Context,
  metric: ComparisonMetric,
  sponsor: SideFigure | null,
  arep: SideFigure | null,
): { sponsorFigure: SideFigure | null; arepFigure: SideFigure | null; varianceNote: string | null; field: string | null } {
  if (!sponsor || !arep || sponsor.field === arep.field) return { sponsorFigure: sponsor, arepFigure: arep, varianceNote: null, field: null }
  const typing = typedValue(ctx, arep.field, metric.unit) !== null
  for (const field of metric.fields) {
    if (typing && field !== arep.field) continue
    const s = sponsorDirect(ctx, field, metric.unit, metric)
    const a = arepDirect(ctx, field, metric.unit, metric)
    if (!s || !a) continue
    const said = (figure: SideFigure, other: SideFigure) => {
      if (field !== metric.fields[0]) figure.notes.unshift(`Stated as the ${field.replace(/_/g, ' ')}.`)
      if (other.field !== field) {
        figure.notes.push(`Also states the ${other.field.replace(/_/g, ' ')}: ${formatFigure(other.value, metric.unit)}.`)
      }
      return figure
    }
    return { sponsorFigure: said(s, sponsor), arepFigure: said(a, arep), varianceNote: null, field }
  }
  const name = (field: string) => `the ${field.replace(/_/g, ' ')}`
  return {
    sponsorFigure: sponsor,
    arepFigure: arep,
    varianceNote: `The sponsor states ${name(sponsor.field)} and ${ctx.client} ${name(arep.field)}: two different quantities, so there is no variance between them.`,
    field: null,
  }
}

/**
 * Which column draws the deal record's value through the EditableSlot.
 *
 * Locked: the column whose figure the deal value is. Unlocked: always the
 * client's — a figure typed on the deal is a person deciding it — except an
 * output, which is never an input (R1): a field the engine derives on the deal
 * (stored, or its live preview), or a ratio the client's own figures give
 * ("Derived 1.25x" from the internal model's NOI and debt service). Those open
 * their inputs instead. The slot shows what is being typed, so while an entry
 * is unsaved it is never drawn under Sponsor: that column keeps the saved figure.
 */
export function slotColumn(row: ComparisonRow, unlocked: boolean): ComparisonSide | null {
  if (unlocked) return row.dealDerivedFormula || isClientOutput(row) ? null : 'arep'
  if (row.dealOwner === 'sponsor' && row.sponsor?.onDeal && !row.typing) return 'sponsor'
  if (row.dealOwner === 'arep' && row.arep?.onDeal) return 'arep'
  return null
}

/** The client's figure is a ratio worked from its own figures, and nobody is typing over it. */
export function isClientOutput(row: ComparisonRow): boolean {
  return row.arep?.origin === 'derived' && !row.typing
}

/** The counts the panel's header reports. */
export function comparisonSummary(rows: readonly ComparisonRow[]) {
  return {
    total: rows.length,
    sponsor: rows.filter((row) => row.sponsor).length,
    arep: rows.filter((row) => row.arep).length,
    differ: rows.filter((row) => row.variance && !row.variance.agrees).length,
  }
}
